/**
 * useBackdropClose — zamykanie okna kliknięciem w tło (overlay) bez „przypadkowych" zamknięć.
 *
 * Przeglądarka wysyła `click` na najbliższego wspólnego przodka miejsca wciśnięcia i puszczenia
 * myszy. Gdy użytkownik wciśnie przycisk w oknie, przeciągnie (np. zaznaczając tekst w polu) i puści
 * poza oknem, `click` ląduje na tle — a samo porównanie `e.target === e.currentTarget` uznaje to za
 * kliknięcie w tło i zamyka okno razem z niezapisanym formularzem. Odwrotnie też: wciśnięcie na tle
 * i puszczenie w oknie daje `click` na tle.
 *
 * Okno zamyka się więc tylko wtedy, gdy ZARÓWNO wciśnięcie, JAK I puszczenie nastąpiły na samym
 * tle. Tak działał rc-dialog w starym WF (tam wciśnięcie w treści blokowało zamknięcie); ten hook
 * obsługuje też drugą stronę (wciśnięcie na tle, puszczenie w oknie). `click` bez wcześniejszego
 * wciśnięcia (klawiatura, czytnik ekranu, kod) zamyka jak dotąd.
 *
 * `click` obsługujemy w fazie przechwytywania (`onClickCapture`): panele okien często wołają
 * `stopPropagation()` na `click`, a bez przechwytywania taki `click` nie zerowałby flagi gestu.
 *
 * Użycie: na górze komponentu (przed wczesnym `return`)
 * `const backdropHandlers = useBackdropClose(onClose, closeOnBackdrop);`, a na elemencie tła
 * `<div {...backdropHandlers}>`. Dziecko wyrenderowane przez portal też liczy się jako „wewnątrz”
 * (zdarzenia React bąbelkują przez drzewo komponentów, więc `e.target !== e.currentTarget`).
 */

import { type MouseEvent, useRef } from 'react';

export interface BackdropCloseHandlers {
  onMouseDown: (e: MouseEvent<HTMLElement>) => void;
  onMouseUp: (e: MouseEvent<HTMLElement>) => void;
  onClickCapture: (e: MouseEvent<HTMLElement>) => void;
}

export function useBackdropClose(onClose: () => void, enabled = true): BackdropCloseHandlers {
  // true = gest zaczął się lub skończył poza tłem → następny `click` na tle ignorujemy.
  const ignoreNextClickRef = useRef(false);

  return {
    onMouseDown: (e) => {
      ignoreNextClickRef.current = e.target !== e.currentTarget;
    },
    onMouseUp: (e) => {
      if (e.target !== e.currentTarget) ignoreNextClickRef.current = true;
    },
    onClickCapture: (e) => {
      const ignore = ignoreNextClickRef.current;
      ignoreNextClickRef.current = false;
      if (!enabled || ignore) return;
      if (e.target === e.currentTarget) onClose();
    },
  };
}
